import clsx from 'clsx'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { StripeSubscriptionInterval, StripeSubscriptionStatus } from '@/account/store/schemas'
import { selectDisplayCostPerSeatInCents, selectHasForeverPercentDiscount } from '@/account/store/selectors'
import { formatCentsToDollarsCompact } from '@/util/money'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { BillingCycle } from '@/firebase/auth/auth-jwt-schema'
import { selectMonthlyPriceForPlan } from '@/checkout/store/selectors'
import { kPaxtonEnterpriseEmailToHref } from '@/constants/constants-strings'
import { kMaxUsersBeforeEnterprisePricing } from '@/organizations/constants'

/**
 * Organization Seat Pricing and Terms
 *
 * Renders the pricing and terms in the organization setup flow.
 * - If there is no discount, the normal per-seat price is shown
 * - If there is a forever-percent discount, the discount is applied to the per-seat price shown
 * - For any other discount (amount-off, non-forever percent, etc.), the per-seat price is shown without the discount,
 *   and a footnote is shown to indicate that the price does not reflect the active discount
 */
export default function OrganizationSeatPricingAndTerms() {
  // Account data
  const initialLoadComplete = useAppSelector((s: RootState) => s.accountState.initialLoadComplete)
  const isLoading = useAppSelector((s: RootState) => s.accountState.loading)

  const subscriptionStatus = useAppSelector((s: RootState) => s.accountState.stripeSubscriptionStatus)
  const subscriptionInterval = useAppSelector((s: RootState) => s.accountState.stripeSubscriptionInterval)
  const hasDiscount = useAppSelector((s: RootState) => s.accountState.hasDiscount)
  const hasForeverPercentDiscount = useAppSelector(selectHasForeverPercentDiscount)
  const displayCostPerSeatInCents = useAppSelector(selectDisplayCostPerSeatInCents)

  // Inferred values
  const isInTrial = subscriptionStatus === StripeSubscriptionStatus.TRIALING
  const intervalAdjective = subscriptionInterval === StripeSubscriptionInterval.MONTH ? 'monthly' : 'annual'
  const showFootnote = hasDiscount && !hasForeverPercentDiscount

  const showDynamicPricing = initialLoadComplete && displayCostPerSeatInCents != null && subscriptionInterval != null
  const showLoader = !initialLoadComplete && isLoading
  const showFallback = !showDynamicPricing && !showLoader

  // Build the organization seat pricing sentence
  const priceText = displayCostPerSeatInCents != null ? `${formatCentsToDollarsCompact(displayCostPerSeatInCents)} per seat/${subscriptionInterval}` : ''
  const sentencePrefix = isInTrial ? "At the end of your trial, you'll be charged " : "You'll be charged "
  const sentenceSuffix = isInTrial
    ? `. This charge will appear on your first ${intervalAdjective} invoice.`
    : ' for each team member who joins. Charges are prorated based on how many days are left in your current billing cycle.'

  // Footnote only when there is a non-forever percent discount (seat price does not reflect the active promo)
  const footnoteText = '* Price estimate does not reflect any active promotions.'

  // Fallback pricing (standard prices), used only if the user's account data failed to load
  const monthlyPlanMonthlyPrice = useAppSelector((state: RootState) => selectMonthlyPriceForPlan(state, { billingCycle: BillingCycle.MONTHLY }))
  const annualPlanMonthlyPrice = useAppSelector((state: RootState) => selectMonthlyPriceForPlan(state, { billingCycle: BillingCycle.ANNUAL }))

  const fallbackSentence = `${formatCentsToDollarsCompact(monthlyPlanMonthlyPrice)} per seat/month, or ${formatCentsToDollarsCompact(
    annualPlanMonthlyPrice
  )} per seat/month when billed annually.`

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <h2 className="font-medium">Pricing & Terms</h2>

      <div className="grid grid-cols-1 md:grid-cols-[auto_auto] justify-start gap-4 md:gap-6 items-stretch mt-1.5">
        <div>
          {/* Left card: Per-seat pricing sentence */}
          <div className="bg-brand-neutral-100 p-3.5 rounded-lg md:h-full flex">
            {/* If the account data is loading, show a loading indicator */}
            {showLoader && (
              <div className="flex justify-center items-center min-h-[24px]" aria-live="polite">
                <CircularProgressContinuousSized size={16} thickness={6} />
              </div>
            )}

            {/* If the account data is loaded, show the pricing sentence */}
            {showDynamicPricing && (
              <p className="text-sm text-brand-neutral-800">
                {sentencePrefix}
                <span className={clsx('font-bold', showFootnote && 'text-brand-800')}>{priceText}</span>
                {showFootnote && <span className="text-brand-800">*</span>}
                {sentenceSuffix}
              </p>
            )}

            {/* Otherwise, show a fallback sentence using standard pricing */}
            {showFallback && (
              <div>
                <h3 className="text-sm font-semibold">Up to {kMaxUsersBeforeEnterprisePricing} users</h3>
                <p className="mt-0.5 text-sm text-brand-neutral-800">{fallbackSentence}</p>
              </div>
            )}
          </div>

          {/* Screens < md:Footnote between the 2 cards, right under the left card */}
          {showDynamicPricing && showFootnote && <p className="md:hidden mt-2 ml-1 text-xs text-brand-800">{footnoteText}</p>}
        </div>

        {/* Right card: Contact for enterprise pricing */}
        <div className="bg-brand-neutral-100 p-3.5 rounded-lg h-full flex flex-col justify-center">
          <h3 className="text-sm font-semibold">More than {kMaxUsersBeforeEnterprisePricing} users?</h3>
          <a href={kPaxtonEnterpriseEmailToHref} className="text-sm font-semibold text-brand-500 hover:text-brand-800 underline mt-0.5 md:whitespace-nowrap">
            Contact us for custom enterprise pricing
          </a>
        </div>
      </div>

      {/* Screens >= md:Footnote under cards section */}
      {showDynamicPricing && showFootnote && <p className="hidden md:block mt-2 text-xs text-brand-800">{footnoteText}</p>}
    </div>
  )
}
